import { HeadContent, Scripts, createRootRoute, Outlet, Link, type ErrorComponentProps } from '@tanstack/react-router'
import { useEffect } from 'react'
import { captureAttribution } from '@/lib/attribution'
import { Toaster } from '@/components/ui/sonner'
import { UpgradePrompt } from '@/components/upgrade-prompt'
import { Providers } from '@/components/providers'
import { themeInitScript } from '@/hooks/use-theme'
import { revealInitScript } from '@/lib/reveal'
import { CatPeeking } from '@/components/mascot'
import { SiteHeader } from '@/components/site-header'
import { SiteFooter } from '@/components/site-footer'
import { reportError } from '@/lib/sentry'
import { ogUrlForPath } from '@/lib/meta'
import { jsonLdScript, organization } from '@/lib/schema'

import appCss from '../styles.css?url'

export const Route = createRootRoute({
  head: ({ matches }) => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { title: 'Uptrack - Uptime Monitoring' },
      { name: 'description', content: 'Monitor your websites, APIs, and services 24/7. Get instant alerts, detailed analytics, and public status pages to keep your services running smoothly.' },
      { property: 'og:title', content: 'Uptrack - Uptime Monitoring' },
      { property: 'og:description', content: 'Monitor your websites, APIs, and services 24/7. Get instant alerts when something goes wrong.' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: ogUrlForPath(matches[matches.length - 1]?.pathname ?? '/') },
      { property: 'og:image', content: 'https://uptrack.app/og/home.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
      // No twitter:title or twitter:description: X reads og:title and og:description, so each page's own text shows.
      { name: 'google-site-verification', content: 'wzGNypZfDfJ4qx_by3ERs1oC0VMyvvD5fiVQpkuwH18' },
    ],
    links: [
      // One variable file covers every UI weight (400-600). Serif and mono load on demand.
      { rel: 'preload', href: '/fonts/instrument-sans-latin.woff2', as: 'font', type: 'font/woff2', crossOrigin: 'anonymous' },
      { rel: 'stylesheet', href: appCss },
      { rel: 'icon', href: '/favicon.svg', type: 'image/svg+xml' },
      { rel: 'icon', href: '/favicon.ico', type: 'image/x-icon' },
      { rel: 'icon', href: '/favicon-96.png', type: 'image/png', sizes: '96x96' },
      { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' },
      { rel: 'llms-txt', href: '/llms.txt' },
    ],
    scripts: [jsonLdScript(organization())],
  }),
  component: RootComponent,
  notFoundComponent: NotFoundPage,
  errorComponent: RootErrorPage,
})

function RootComponent() {
  useEffect(() => {
    captureAttribution()
  }, [])

  return (
    <RootDocument>
      <Outlet />
    </RootDocument>
  )
}

function RootErrorPage({ error, reset }: ErrorComponentProps) {
  useEffect(() => {
    reportError(error, { source: 'router' })
  }, [error])

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
      <h1 className="text-4xl font-semibold">Something went wrong</h1>
      <p className="max-w-md text-muted-foreground">
        {error instanceof Error ? error.message : 'An unexpected error occurred.'}
      </p>
      <button
        onClick={reset}
        className="mt-4 inline-flex h-10 items-center justify-center rounded-md bg-cta px-6 text-sm font-medium text-cta-foreground hover:opacity-90"
      >
        Try Again
      </button>
    </div>
  )
}

function NotFoundPage() {
  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <SiteHeader />
      <main className="flex flex-1 flex-col items-center justify-center gap-4 px-4 py-16 text-center">
        <CatPeeking size={180} />
        <p className="num text-sm text-muted-foreground">404</p>
        <h1 className="text-3xl font-semibold tracking-tight">This page does not exist</h1>
        <p className="max-w-md text-muted-foreground">
          The link may be old, or the page has moved. Try the homepage or the docs.
        </p>
        <div className="mt-4 flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            className="inline-flex h-10 items-center justify-center rounded-md bg-cta px-6 text-sm font-medium text-cta-foreground hover:opacity-90"
          >
            Go to homepage
          </Link>
          <Link
            to="/docs"
            className="inline-flex h-10 items-center justify-center rounded-md border border-border px-6 text-sm font-medium hover:bg-accent"
          >
            Read the docs
          </Link>
        </div>
      </main>
      <SiteFooter />
    </div>
  )
}

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
        {/* Prevent FOUC: apply theme class before any CSS is evaluated.
            Uses a static string (not Function.toString) to avoid build-tool artifacts. */}
        <script dangerouslySetInnerHTML={{ __html: themeInitScript }} />
        {/* Same idea for the marketing scroll reveal: mark <html> before first
            paint so sections never flash visible-then-hidden. No-ops under
            reduced-motion or without IntersectionObserver, which leaves every
            section visible. */}
        <script dangerouslySetInnerHTML={{ __html: revealInitScript }} />
      </head>
      <body className="min-h-screen bg-background font-sans antialiased">
        <Providers>
          {children}
          <Toaster position="top-right" duration={6000} />
          <UpgradePrompt />
        </Providers>
        <Scripts />
        <ChatwootWidget />
      </body>
    </html>
  )
}

function ChatwootWidget() {
  // Load the support chat once the page is idle, so it does not compete with
  // the first render. It renders nothing until both env values are set.
  useEffect(() => {
    const baseUrl = (import.meta.env.VITE_CHATWOOT_BASE_URL as string | undefined)?.replace(/\/+$/, '')
    const websiteToken = import.meta.env.VITE_CHATWOOT_WEBSITE_TOKEN as string | undefined
    if (!baseUrl || !websiteToken) return
    let script: HTMLScriptElement | null = null
    const load = () => {
      script = document.createElement('script')
      script.async = true
      script.src = `${baseUrl}/packs/js/sdk.js`
      script.onload = () => window.chatwootSDK?.run({ websiteToken, baseUrl })
      document.body.appendChild(script)
    }
    const idle = 'requestIdleCallback' in window
    const id = idle ? window.requestIdleCallback(load, { timeout: 5000 }) : window.setTimeout(load, 3000)
    return () => {
      if (idle) window.cancelIdleCallback(id)
      else window.clearTimeout(id)
      script?.remove()
    }
  }, [])
  return null
}

declare global {
  interface Window {
    chatwootSDK?: { run: (config: { websiteToken: string; baseUrl: string }) => void }
  }
}
